import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import PublicHome from "@/pages/PublicHome";
import PublicEnrollment from "@/pages/PublicEnrollment";
import Login from "@/pages/Login";
import Access from "@/pages/Access";
import PublicHistory from "@/pages/PublicHistory";
import PublicDailyLinks from "@/pages/PublicDailyLinks";
import SiteContent from "@/pages/SiteContent";
import Dashboard from "@/pages/Dashboard";
import Courses from "@/pages/Courses";
import Teachers from "@/pages/Teachers";
import Availability from "@/pages/Availability";
import Rooms from "@/pages/Rooms";
import Schedule from "@/pages/Schedule";
import Holidays from "@/pages/Holidays";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Students from "@/pages/Students";
import Guardians from "@/pages/Guardians";
import Enrollments from "@/pages/Enrollments";
import Leads from "@/pages/Leads";
import Requests from "@/pages/Requests";
import WhatsAppSimulator from "@/pages/WhatsAppSimulator";
import Financial from "@/pages/Financial";
import Announcements from "@/pages/Announcements";
import Portal from "@/pages/Portal";
import Albums from "@/pages/Albums";

const PORTAL_ROLES = ["aluno", "responsavel"];

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="loading-screen">
        <span className="font-heading text-2xl font-extrabold tracking-tighter">
          IMJ<span className="text-primary">.</span>
        </span>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) {
    return <Navigate to={PORTAL_ROLES.includes(user.role) ? "/portal" : "/admin"} replace />;
  }
  return children;
}

const VIEW_ROLES = ["admin", "direcao", "secretaria"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicHome />} />
          <Route path="/matricula" element={<PublicEnrollment />} />
          <Route path="/login" element={<Login />} />
          <Route path="/acesso" element={<Access />} />
          <Route path="/nossa-historia" element={<PublicHistory />} />
          <Route path="/links-do-dia-a-dia" element={<PublicDailyLinks />} />
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <RoleRoute roles={PORTAL_ROLES}>
                  <Portal />
                </RoleRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<RoleRoute roles={["admin", "direcao", "secretaria", "professor"]}><Dashboard /></RoleRoute>} />
            <Route path="agenda" element={<Schedule />} />
            <Route path="alunos" element={<RoleRoute roles={VIEW_ROLES}><Students /></RoleRoute>} />
            <Route path="responsaveis" element={<RoleRoute roles={VIEW_ROLES}><Guardians /></RoleRoute>} />
            <Route path="matriculas" element={<RoleRoute roles={["admin", "direcao", "secretaria", "professor"]}><Enrollments /></RoleRoute>} />
            <Route path="interessados" element={<RoleRoute roles={VIEW_ROLES}><Leads /></RoleRoute>} />
            <Route path="solicitacoes" element={<RoleRoute roles={["admin", "direcao", "secretaria", "professor"]}><Requests /></RoleRoute>} />
            <Route path="comunicados" element={<RoleRoute roles={["admin", "direcao", "secretaria", "professor"]}><Announcements /></RoleRoute>} />
            <Route path="whatsapp" element={<RoleRoute roles={VIEW_ROLES}><WhatsAppSimulator /></RoleRoute>} />
            <Route path="financeiro" element={<RoleRoute roles={VIEW_ROLES}><Financial /></RoleRoute>} />
            <Route path="cursos" element={<RoleRoute roles={VIEW_ROLES}><Courses /></RoleRoute>} />
            <Route path="professores" element={<RoleRoute roles={VIEW_ROLES}><Teachers /></RoleRoute>} />
            <Route path="fotos" element={<RoleRoute roles={["admin", "direcao"]}><Albums /></RoleRoute>} />
            <Route path="conteudo" element={<RoleRoute roles={["admin", "direcao"]}><SiteContent /></RoleRoute>} />
            <Route path="disponibilidade" element={<RoleRoute roles={VIEW_ROLES}><Availability /></RoleRoute>} />
            <Route path="salas" element={<RoleRoute roles={VIEW_ROLES}><Rooms /></RoleRoute>} />
            <Route path="feriados" element={<RoleRoute roles={VIEW_ROLES}><Holidays /></RoleRoute>} />
            <Route path="usuarios" element={<RoleRoute roles={["admin"]}><Users /></RoleRoute>} />
            <Route path="configuracoes" element={<RoleRoute roles={["admin"]}><Settings /></RoleRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
